import type React from 'react';
import {useMemo, useState} from 'react';
import {Alert, Box, Button, Paper, Skeleton, Stack, Typography} from '@mui/material';
import {OpenInNew} from '@mui/icons-material';
import type {GridColDef, GridPaginationModel, GridRowId, GridSortModel} from '@mui/x-data-grid-premium';
import {generatePath, useNavigate, useOutletContext} from 'react-router';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import {formatDate} from '~/pages/category-profile/domain/formatDate';
import {SegmentedControl} from '~/components/segmented-control/SegmentedControl';
import {Tag} from '~/components/tag/Tag';
import {routes} from '~/routing/routes';
import {perUnit} from '../../domain/unitPrice';
import {useBenchmarksQuery} from '../../hooks/useCategoryProfileQueries';
import {usePriceFormat} from '../../hooks/usePriceFormat';
import type {BenchmarkConfidenceFilter, BenchmarkGroup, BenchmarkOrdering} from '../../types/benchmarkTypes';
import {BenchmarkSitesPanel} from '../benchmarks/BenchmarkSitesPanel';
import {SkuPriceChart} from '../benchmarks/SkuPriceChart';
import {ConfidenceBadge} from '../components/ConfidenceBadge';
import {ProfileSection} from '../components/ProfileSection';
import type {CategoryProfileOutletContext} from '../profileTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const FILTERS: {label: string; value: BenchmarkConfidenceFilter}[] = [
    {label: 'All', value: 'all'}, {label: 'High confidence', value: 'high'}, {label: 'Indicative', value: 'indicative'},
];
const SORTABLE = new Set(['label', 'spend', 'spreadPct', 'saving', 'confidence']);

/** The build window's end is exclusive (the first day after it); show the last day inside it. */
function lastDay(end: string | null): string | null {
    if (!end) return null;
    const d = new Date(`${end}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}

function ordering(model: GridSortModel): BenchmarkOrdering {
    const s = model[0];
    if (!s || !SORTABLE.has(s.field)) return '-saving';
    return `${s.sort === 'asc' ? '' : '-'}${s.field}` as BenchmarkOrdering;
}

/**
 * Benchmarks (spec 4.11, as scoped in the build plan): this customer's own prices only. Internal price variance per
 * item across sites, and price per SKU. Cross-customer cohorts are out of scope.
 */
export const BenchmarksTab: React.FC = () => {
    const {databagId, path, header, money} = useOutletContext<CategoryProfileOutletContext>();
    const price = usePriceFormat();
    const navigate = useNavigate();
    const [filter, setFilter] = useState(0);
    const [pagination, setPagination] = useState<GridPaginationModel>({page: 0, pageSize: 10});
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'saving', sort: 'desc'}]);
    const [expanded, setExpanded] = useState<Set<GridRowId>>(() => new Set());
    const {data, isLoading, isFetching, isError} = useBenchmarksQuery(databagId, {
        path, confidence: FILTERS[filter].value, ordering: ordering(sortModel), page: pagination.page, pageSize: pagination.pageSize,
    });
    // Prices are compared across sites, or business units when the dataset has no site column
    const comparison = data?.comparison;
    const fallback = comparison?.dimension === 'business_unit';
    const place = fallback ? {one: 'business unit', many: 'business units', fallback} : {one: 'site', many: 'sites', fallback};

    const columns: GridColDef<BenchmarkGroup>[] = useMemo(() => [
        {field: 'label', headerName: 'Item', flex: 1, minWidth: 240, renderCell: ({row}) => (
            <Stack direction="row" gap={1} alignItems="center" sx={{minWidth: 0}}>
                <Typography variant="body2" noWrap title={row.label}>{row.label}</Typography>
                <Tag size="small" solid={false} color="default" label={row.methodLabel}/>
            </Stack>
        )},
        {field: 'bestPrice', headerName: 'Lowest (best)', width: 140, sortable: false, align: 'right', headerAlign: 'right',
            valueFormatter: (v: number, r) => perUnit(price(v), r.unit)},
        {field: 'highestPrice', headerName: 'Highest', width: 140, sortable: false, align: 'right', headerAlign: 'right',
            valueFormatter: (v: number, r) => perUnit(price(v), r.unit)},
        {field: 'spreadPct', headerName: 'Spread', type: 'number', width: 90, valueFormatter: (v: number) => `${v.toFixed(0)}%`},
        {field: 'bestSite', headerName: `Best-price ${place.one}`, width: 160, sortable: false},
        {field: 'spend', headerName: 'Spend', type: 'number', width: 110, valueFormatter: (v: number) => money(v)},
        {field: 'saving', headerName: 'Saving at best price', type: 'number', width: 160, renderCell: ({row}) => (
            <Typography variant="body2" color={row.indicative ? 'text.secondary' : 'text.primary'}
                        title={row.savingCapped ? 'Capped at 30% of the item\'s spend' : undefined}>
                {row.saving ? money(row.saving) : '—'}{row.savingCapped ? '*' : ''}
            </Typography>
        )},
        {field: 'confidence', headerName: 'Confidence', width: 110, align: 'center', headerAlign: 'center',
            renderCell: ({row}) => <ConfidenceBadge score={row.confidence} reasons={row.confidenceReasons}/>},
    ], [money, price, place.one]);

    if (isError) return <Alert severity="error">Could not load the benchmarks.</Alert>;
    if (isLoading || !data) return <Skeleton variant="rounded" height={420}/>;

    const s = data.summary;
    const openPpv = () => navigate(generatePath(routes.analytics_koi_ppv, {id: String(databagId)}));

    if (!data.build) {
        return <Alert severity="info">Benchmarks appear after the next data build of this dataset.</Alert>;
    }
    const tiles = [
        {label: 'Saving at best price', value: money(s.saving), hint: `${s.highConfidence} high-confidence items`},
        {label: 'Items benchmarked', value: s.groups.toLocaleString(LOCALE), hint: `${s.groups - s.highConfidence} indicative`},
        {label: 'Spend covered', value: s.coveragePct === null ? '—' : `${s.coveragePct.toFixed(0)}%`,
            hint: `${money(s.spendBenchmarked)} of ${money(s.categorySpend)}`},
        {label: 'Indicative saving', value: money(s.savingIndicative), hint: 'Low confidence, not counted'},
    ];

    return (
        <Stack gap={2}>
            {comparison && !comparison.quantityInUse && (
                <Alert severity="warning">
                    This dataset has no quantity column in use, so there are no unit prices to compare. Switch on the
                    quantity and unit columns in the dataset&apos;s column configuration, then rebuild.
                </Alert>
            )}
            <Alert severity="info">
                {`Internal benchmark: what your own ${place.many} pay for the same item in ${header.name}, ${formatDate(data.build.windowStart)} to ${formatDate(lastDay(data.build.windowEnd))}. `}
                {`Items match on material code, Material Harmonization cluster or description; the best price is what the cheapest ${place.many} (10% of volume) pay. `}
                Items below 60 confidence are indicative and not counted in totals.
                {place.fallback && ' This dataset has no site column in use, so prices are compared across business units.'}
                {comparison && comparison.skippedOneAtATime > 0
                    && ` ${comparison.skippedOneAtATime.toLocaleString(LOCALE)} items bought one at a time (services, one-off purchases) are left out.`}
            </Alert>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr 1fr', md: 'repeat(4, 1fr)'}, gap: 1.5}}>
                {tiles.map(t => (
                    <Paper key={t.label} variant="outlined" sx={{p: 1.5}}>
                        <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                        <Typography variant="h6">{t.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.hint}</Typography>
                    </Paper>
                ))}
            </Box>

            <ProfileSection
                title="Internal price variance"
                subtitle={`The same item bought at different prices across sites · ${money(s.saving)} if every site paid the best price`}
                actions={<Button size="small" endIcon={<OpenInNew fontSize="small"/>} onClick={openPpv}>Open in Purchase Price Variances</Button>}
            >
                {s.groups === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No items in this category could be compared. Lines need a material code, a Material Harmonization
                        cluster or a clear description, plus a quantity and a unit.
                    </Typography>
                ) : (
                    <Stack gap={1.5}>
                        <SegmentedControl labels={FILTERS.map(f => f.label)} value={filter} sx={{alignSelf: 'flex-start'}}
                                          onChange={i => {
                                              setFilter(i);
                                              setPagination(p => ({...p, page: 0}));
                                          }}/>
                        {/* Rows (36px) of this page, plus header, toolbar, pagination and any expanded rows */}
                        <Box sx={{height: Math.min(720, Math.max(2, data.rows.length) * 36 + 140 + expanded.size * 200)}}>
                            <CustomDataGrid
                                layoutKey="category-profile-benchmarks"
                                rows={data.rows} columns={columns} loading={isFetching} getRowId={r => r.id}
                                rowCount={data.count} paginationMode="server" sortingMode="server"
                                paginationModel={pagination} onPaginationModelChange={setPagination}
                                sortModel={sortModel} onSortModelChange={m => {
                                    setSortModel(m);
                                    setPagination(p => ({...p, page: 0}));
                                }}
                                pageSizeOptions={[10, 25, 50]}
                                disableRowSelectionOnClick showToolbar
                                toolbarProps={{title: `${data.count.toLocaleString(LOCALE)} item${data.count === 1 ? '' : 's'}`, showFilterPanel: false}}
                                detailPanelExpandedRowIds={expanded}
                                onDetailPanelExpandedRowIdsChange={setExpanded}
                                getDetailPanelHeight={() => 'auto'}
                                getDetailPanelContent={({row}) => (
                                    <BenchmarkSitesPanel databagId={databagId} path={path} id={row.id} unit={row.unit}
                                                         money={money} price={price} withoutSites={fallback}/>
                                )}
                                localeText={{noRowsLabel: 'No items match.'}}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            * Saving capped at 30% of the item&apos;s spend. Prices are per base unit (piece, kg, litre, m, …).
                        </Typography>
                    </Stack>
                )}
            </ProfileSection>

            <ProfileSection title="Price breakdown per SKU" subtitle="The 15 items with most spend">
                <SkuPriceChart skus={data.skus} price={price} place={place.many}/>
            </ProfileSection>
        </Stack>
    );
};
